import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Conversation, Message } from '../types';
import { messagesApi } from '../services/api';

const Messages: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'businesses' | 'users'>('businesses');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const businessIdFilter = searchParams.get('businessId') || '';

  useEffect(() => {
    loadConversations();
  }, [activeTab]);

  useEffect(() => {
    if (!businessIdFilter) return;
    setActiveTab('businesses');
  }, [businessIdFilter]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch conversations from API
      const response = await messagesApi.getConversations(1);
      
      if (response.success) {
        setConversations(response.data || []);
      } else {
        setError(response.error || 'Failed to load conversations');
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const response = await messagesApi.getMessages(conversationId);
      if (response.success) {
        setMessages(response.data || []);
        // Mark as read
        await messagesApi.markAsRead(conversationId);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      const response = await messagesApi.sendMessage(selectedConversation.id, newMessage);
      if (response.success) {
        setNewMessage('');
        loadMessages(selectedConversation.id); // Refresh messages
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  // NOTE: Demo data removed - now using live API data
  // Keeping this comment for reference of old structure:
  /*
  const demoConversations: Conversation[] = [
    {
      id: 'conv-1',
      businessId: 'biz-1',
      businessName: 'Rainbow Sails Art',
      ...
    },
  ];
  */
  
  // NOTE: Demo data removed - using live API data

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredConversations = conversations.filter(c => 
    (c.businessName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) &&
    (!businessIdFilter || c.businessId === businessIdFilter)
  );

  useEffect(() => {
    if (!businessIdFilter || conversations.length === 0) return;
    const match = conversations.find((c) => c.businessId === businessIdFilter);
    if (match && selectedConversation?.id !== match.id) {
      setSelectedConversation(match);
    }
  }, [businessIdFilter, conversations, selectedConversation?.id]);

  const canMessageUsers = user && ['social', 'pro', 'premium'].includes(user.subscription);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 160px)', gap: '20px' }}>
      {/* Conversation List */}
      <div style={{
        width: '350px',
        background: 'white',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 15, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>💬 Mango Messaging</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <a href={process.env.REACT_APP_CHAT_APP_URL || 'https://chat.mango-business.solutions'} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'linear-gradient(135deg, #ff9a56, #ff6b35)', color: 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none', boxShadow: '0 2px 8px rgba(255,107,53,0.3)' }}><span>💬</span> Chat</a>
              <a href={process.env.REACT_APP_FEED_APP_URL || 'https://feed.mango-business.solutions'} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#27272a', color: 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><span>📰</span> Feed</a>
              <a href={process.env.REACT_APP_CALENDAR_APP_URL || 'https://calendar.mango-business.solutions'} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#27272a', color: 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><span>📅</span> Calendar</a>
              <a href={process.env.REACT_APP_SHOP_APP_URL || 'https://shop.mango-business.solutions'} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#27272a', color: 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}><span>🛒</span> Shop</a>
            </div>
          </div>
          
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
            <button
              onClick={() => setActiveTab('businesses')}
              style={{
                flex: 1,
                padding: '10px',
                background: activeTab === 'businesses' ? '#ff6b35' : '#f0f0f0',
                color: activeTab === 'businesses' ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === 'businesses' ? '600' : '400'
              }}
            >
              🏪 Businesses
            </button>
            <button
              onClick={() => setActiveTab('users')}
              disabled={!canMessageUsers}
              style={{
                flex: 1,
                padding: '10px',
                background: activeTab === 'users' ? '#667eea' : '#f0f0f0',
                color: activeTab === 'users' ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: canMessageUsers ? 'pointer' : 'not-allowed',
                fontSize: '13px',
                fontWeight: activeTab === 'users' ? '600' : '400',
                opacity: canMessageUsers ? 1 : 0.5
              }}
            >
              👤 Users
              {!canMessageUsers && <span style={{ marginLeft: '5px' }}>🔒</span>}
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              fontSize: '14px'
            }}
          />
          {businessIdFilter && (
            <button
              onClick={() => navigate('/messages')}
              style={{
                marginTop: 10,
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #fed7aa',
                borderRadius: 10,
                background: '#fff7ed',
                color: '#9a3412',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Clear Business Filter
            </button>
          )}
        </div>

        {/* Conversation List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              <span style={{ fontSize: '36px', display: 'block', marginBottom: '10px' }}>💬</span>
              No conversations yet
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                style={{
                  padding: '15px 20px',
                  cursor: 'pointer',
                  background: selectedConversation?.id === conv.id ? '#fff3e0' : 'transparent',
                  borderBottom: '1px solid #f0f0f0',
                  borderLeft: selectedConversation?.id === conv.id ? '3px solid #ff6b35' : '3px solid transparent',
                  transition: 'background 0.15s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        flexShrink: 0
                      }}>
                        {conv.businessName.substring(0, 2).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ 
                          margin: 0, 
                          fontWeight: conv.unreadCount > 0 ? '700' : '600', 
                          color: '#333',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {conv.businessName}
                        </p>
                        <p style={{ 
                          margin: '4px 0 0 0', 
                          fontSize: '12px', 
                          color: conv.unreadCount > 0 ? '#333' : '#888',
                          fontWeight: conv.unreadCount > 0 ? '500' : '400',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {conv.lastMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: '10px', flexShrink: 0 }}>
                    <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>
                      {formatTime(conv.lastMessageAt)}
                    </p>
                    {conv.unreadCount > 0 && (
                      <span style={{
                        display: 'inline-block',
                        marginTop: '5px',
                        background: '#ff6b35',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '10px'
                      }}>
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* User messaging upgrade prompt */}
        {activeTab === 'users' && !canMessageUsers && (
          <div style={{ 
            padding: '20px', 
            background: 'linear-gradient(135deg, #667eea15, #764ba215)',
            borderTop: '1px solid #667eea30',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#333' }}>
              Upgrade to Mango Social to message other users
            </p>
            <button
              onClick={() => navigate('/upgrade')}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Upgrade for $3/mo
            </button>
          </div>
        )}
      </div>

      {/* Message View */}
      <div style={{
        flex: 1,
        background: 'white',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div style={{ 
              padding: '20px', 
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <div style={{
                width: '45px',
                height: '45px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ff9a56, #ff6b35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {selectedConversation.businessName.substring(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                  {selectedConversation.businessName}
                </h3>
                <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#888' }}>
                  {selectedConversation.businessMangoId} • {selectedConversation.status === 'open' ? '🟢 Active' : '⚪ Closed'}
                </p>
              </div>
              <button
                onClick={() => navigate(`/businesses/${selectedConversation.businessId}`)}
                style={{
                  padding: '10px 16px',
                  background: '#f0f0f0',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#333'
                }}
              >
                View Business
              </button>
            </div>

            {/* Messages */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '20px',
              background: '#f8f9fa'
            }}>
              {messages.map((msg, index) => {
                const isMe = msg.senderType === 'customer';
                const showDate = index === 0 || 
                  new Date(msg.createdAt).toDateString() !== new Date(messages[index - 1].createdAt).toDateString();
                
                return (
                  <React.Fragment key={msg.id}>
                    {showDate && (
                      <div style={{ 
                        textAlign: 'center', 
                        margin: '20px 0',
                        color: '#888',
                        fontSize: '12px'
                      }}>
                        {new Date(msg.createdAt).toLocaleDateString('en-AU', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: isMe ? 'flex-end' : 'flex-start',
                      marginBottom: '15px'
                    }}>
                      <div style={{
                        maxWidth: '70%',
                        padding: '14px 18px',
                        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        background: isMe 
                          ? 'linear-gradient(135deg, #ff9a56, #ff6b35)'
                          : 'white',
                        color: isMe ? 'white' : '#333',
                        boxShadow: isMe ? 'none' : '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '14px', 
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {msg.content}
                        </p>
                        <p style={{ 
                          margin: '8px 0 0 0', 
                          fontSize: '10px', 
                          opacity: 0.7,
                          textAlign: 'right'
                        }}>
                          {new Date(msg.createdAt).toLocaleTimeString('en-AU', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div style={{ 
              padding: '20px', 
              borderTop: '1px solid #eee',
              background: 'white'
            }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '24px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  style={{
                    padding: '14px 24px',
                    background: newMessage.trim() 
                      ? 'linear-gradient(135deg, #ff9a56, #ff6b35)'
                      : '#e0e0e0',
                    color: newMessage.trim() ? 'white' : '#999',
                    border: 'none',
                    borderRadius: '24px',
                    cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </>
        ) : (
          /* No conversation selected */
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: '#666'
          }}>
            <span style={{ fontSize: '64px', marginBottom: '20px' }}>💬</span>
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Select a conversation</h3>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Choose a conversation from the list to start messaging
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
